'use client';

import { ChangeEvent } from 'react';
import {
  Checkbox,
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { stickNameCell, StyledHeaderTableCell, StyledTable } from '~/shared';
import { CampaignReport } from '~/types';

import { useGetReportsCampaigns } from '../../hooks';

import { ReportCampaignsRow } from './Row';

export type ReportsCampaignsResponse = {
  list?: CampaignReport[];
  total: number;
};

export type ReportCampaignsListProps = {
  getReportsCampaignsQuery: ReturnType<typeof useGetReportsCampaigns>;
  advertiserId: string;
  selectedCampaigns: string[];
  onSelectCampaign: (campaignId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
};

export function ReportCampaignsList({
  getReportsCampaignsQuery,
  advertiserId,
  selectedCampaigns,
  onSelectCampaign,
  onSelectAll,
}: ReportCampaignsListProps) {
  const { data, isLoading, isError, error } = getReportsCampaignsQuery;

  const campaigns = data?.list || [];
  const allSelected =
    campaigns.length > 0 &&
    campaigns.every((campaign: CampaignReport) =>
      selectedCampaigns.includes(campaign.idCampaign)
    );
  const someSelected = campaigns.some((campaign: CampaignReport) =>
    selectedCampaigns.includes(campaign.idCampaign)
  );

  return (
    <StyledTable aria-label="Tabela de Relatórios">
      <TableHead>
        <TableRow>
          <StyledHeaderTableCell sx={{ width: '48px' }} align="center">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onSelectAll(e.target.checked)
              }
              disabled={isLoading || campaigns.length === 0}
            />
          </StyledHeaderTableCell>
          <StyledHeaderTableCell sx={{ width: '60px' }} align="center">
            ID
          </StyledHeaderTableCell>
          <StyledHeaderTableCell sx={stickNameCell()}>
            Campanha
          </StyledHeaderTableCell>
          <StyledHeaderTableCell>Plataforma</StyledHeaderTableCell>
          <StyledHeaderTableCell>Impressões</StyledHeaderTableCell>
          <StyledHeaderTableCell>Cliques</StyledHeaderTableCell>
          <StyledHeaderTableCell>CTR</StyledHeaderTableCell>
          <StyledHeaderTableCell>CPM</StyledHeaderTableCell>
          <StyledHeaderTableCell>CPC</StyledHeaderTableCell>
          {/* <StyledHeaderTableCell align="center">Download</StyledHeaderTableCell> */}
        </TableRow>
      </TableHead>

      <TableBody>
        {isLoading && (
          <TableRow>
            <TableCell component="th" scope="row" colSpan={9} align="center">
              <CircularProgress size={15} /> Carregando
            </TableCell>
          </TableRow>
        )}

        {!isLoading && !isError && data?.list?.length === 0 && (
          <TableRow>
            <TableCell colSpan={9} align="center">
              Sem resultados
            </TableCell>
          </TableRow>
        )}

        {isError && (
          <TableRow>
            <TableCell
              sx={{ color: theme => theme.palette.error.light }}
              colSpan={9}
              align="center"
            >
              <Typography>Ocorreu um erro: {error.name}</Typography>
              <Typography>{error.message}</Typography>
            </TableCell>
          </TableRow>
        )}

        {!isLoading &&
          !isError &&
          data?.list?.map((resume: CampaignReport) => {
            return (
              <ReportCampaignsRow
                key={resume.idCampaign}
                resume={resume}
                advertiserId={advertiserId}
                isSelected={selectedCampaigns.includes(resume.idCampaign)}
                onSelect={(selected: boolean) =>
                  onSelectCampaign(resume.idCampaign, selected)
                }
              />
            );
          })}
      </TableBody>
    </StyledTable>
  );
}
